import { config } from "./config";

export type SlateGame = {
  game_id: string;
  game_date: string;
  home_team: string;
  away_team: string;
  p_home_baseline: number;
  p_home_user?: number | null;
  delta_user_vs_baseline?: number | null;
  coverage?: number | null;
  low_confidence?: boolean | null;
  n_nan_features?: number | null;
  // Betting decision columns written by advise_slate into predictions.parquet
  offered_ml?: number | null;
  ev?: number | null;
  kelly_capped?: number | null;
  bet?: boolean | null;
  edge?: "no_bet" | "lean" | "strong" | null;
  // Per-model (user) betting columns — spec 007. The UI MUST read these via
  // meta.field_ev/etc through resolveBettingFields, never by hardcoded key.
  ev_user?: number | null;
  kelly_capped_user?: number | null;
  bet_user?: boolean | null;
  edge_user?: "no_bet" | "lean" | "strong" | null;
  // Market lines (present when silver game table has them)
  opening_spread_home?: number | null;
  opening_total?: number | null;
  // Margin model outputs (present when bundle includes margin_model)
  mu_margin?: number | null;
  model_spread_home?: number | null;
  ats_gap?: number | null;
  // Totals model outputs (present when bundle includes totals_model)
  mu_total?: number | null;
  ou_edge?: number | null;
  /** Research / DVOA-style tracks joined when PLATFORM_RESEARCH_* prediction URIs are set */
  p_home_research_dvoa_box_impact?: number | null;
  mu_total_research_dvoa_box_impact?: number | null;
  p_home_research_epm_style?: number | null;
  mu_total_research_epm_style?: number | null;
  /** UTC ISO-8601 timestamp of the score run that most recently moved p_home_baseline
   *  for this row by more than the score-job's threshold. Rows with this equal to the
   *  most recent value across the slate are the ones whose BASELINE prediction moved. */
  prediction_changed_at?: string | null;
  /** Same idea, per research track. Populated when the research parquet carries
   *  a prediction_changed_at column. */
  prediction_changed_at_dvoa_box_impact?: string | null;
  prediction_changed_at_epm_style?: string | null;
  // Research per-model betting columns — spec 007. Keys are dynamic
  // (`<field>_research_<id>`); read them via meta.field_*, never hardcoded.
  [key: `ev_research_${string}`]: number | null | undefined;
  [key: `kelly_capped_research_${string}`]: number | null | undefined;
  [key: `bet_research_${string}`]: boolean | null | undefined;
  [key: `edge_research_${string}`]:
    | "no_bet"
    | "lean"
    | "strong"
    | null
    | undefined;
  [key: `ou_edge_research_${string}`]: number | null | undefined;
};

export type SlateResearchModel = {
  id: string;
  label_short: string;
  explain?: string | null;
  field_p_home: string;
  field_mu_total?: string | null;
};

export type SlateBettingModelSource = "baseline_precomputed" | "api_live";

/** One entry in `betting_models[]` — describes which JSON keys on a
 *  SlateGame carry this model's betting fields (spec 007). The UI reads
 *  cells via these `field_*` pointers, never by a hardcoded column name. */
export type SlateBettingModel = {
  id: string; // "baseline" | "user" | "research_<id>"
  label: string; // display label (matches the prediction column header)
  source: SlateBettingModelSource;
  has_spread: boolean;
  has_total: boolean;
  field_ev: string;
  field_kelly_capped: string;
  field_bet: string;
  field_edge: string;
  field_mu_total?: string | null;
  field_ou_edge?: string | null;
  field_model_spread_home?: string | null;
  field_ats_gap?: string | null;
};

export type SlateWindow = {
  from: string | null;
  to: string | null;
  /** Present only when the API used the implicit today..today+N window. */
  default_days?: number | null;
  /** When true, final games and past dates are excluded from the slate. */
  upcoming_only?: boolean | null;
};

export type SlateResponse = {
  league_id: string;
  generated_at: string;
  has_user_model: boolean;
  baseline?: {
    uri?: string | null;
    info_uri?: string | null;
    info?: Record<string, unknown> | null;
    explain?: string | null;
  } | null;
  research_models?: SlateResearchModel[];
  /** Per-model betting registry (spec 007). Optional: absent against an
   *  older API, in which case the UI synthesises a baseline-only entry. */
  betting_models?: SlateBettingModel[];
  games: SlateGame[];
  window?: SlateWindow;
};

export type SlateQueryOpts = {
  from?: string;
  to?: string;
  all?: boolean;
  /** Default on the API is true; set false to include completed games in range. */
  upcoming?: boolean;
};

export type PredictionHistoryGame = {
  league_game_id: string;
  game_date: string;
  home_team_id: string;
  away_team_id: string;
  p_home_base: number | null;
  home_win_actual: number | null;
  predicted_home_win: number | null;
  correct: boolean | null;
};

export type PredictionHistoryFilter = {
  season: string | null;
  all_history: boolean;
  calendar_year_only: boolean;
  current_year: number;
  row_limit: number;
};

export type PredictionHistoryResponse = {
  league_id: string;
  generated_at: string;
  games: PredictionHistoryGame[];
  summary: {
    n_games: number;
    n_correct: number;
    accuracy: number | null;
  };
  filter?: PredictionHistoryFilter;
};

export type PredictionHistoryOpts = RequestOpts & {
  season?: string;
  /** Include all seasons (training-era rows); default is current calendar year only. */
  allHistory?: boolean;
  limit?: number;
};

export type ResultsWindow = "std" | "d30" | "d7";

export type ModelMetrics = {
  logLoss: number | null;
  brier: number | null;
  pickAcc: number | null;
  roi: number | null;
  bets: [number, number, number];
  units: number;
  totalStaked: number;
};

export type ModelSeries = {
  cum_log_loss: number[];
  cum_brier: number[];
  cum_pick_acc: number[];
  cum_roi: number[];
};

export type ModelCalibrationBucket = {
  bucket: number;
  predicted: number;
  observed: number;
  n: number;
};

export type ModelPerformance = {
  id: string;
  label: string;
  metrics: ModelMetrics;
  series: ModelSeries;
  calibration: ModelCalibrationBucket[];
};

export type GradedGameBet = {
  side: "home" | "away" | null;
  outcome: "W" | "L" | "P" | null;
  stake: number;
  profit: number;
};

export type GradedGamePick = {
  pick: string;
  prob: number;
  correct: boolean;
  bet: GradedGameBet | null;
};

export type GradedGame = {
  league_game_id: string;
  date: string;
  home_team_id: string;
  away_team_id: string;
  winner: "home" | "away" | null;
  picks: Record<string, GradedGamePick>;
};

export type ResultsResponse = {
  league_id: string;
  window: ResultsWindow;
  models: ModelPerformance[];
  games: GradedGame[];
  season_tally: {
    games_graded: number;
    bets_placed: number;
  };
};

export type FeatureGroupWeights = Record<string, number>;

export type ModelConfig = {
  config_id: string;
  user_key: string;
  league_id: string;
  label?: string | null;
  display_name?: string | null;
  note?: string | null;
  feature_group_weights: FeatureGroupWeights;
  created_at: string;
};

export type TimeSplit = {
  train_end_date: string;
  eval_start_date: string;
  eval_end_date: string;
};

export type ModelLabSplitPreview = {
  split: TimeSplit;
  labeled_game_date_range: { min: string | null; max: string | null };
  counts: { n_labeled_total: number; n_train: number; n_eval: number };
  ok: boolean;
  errors: string[];
  warnings: string[];
  suggested_split: TimeSplit | null;
};

export type ModelLabSplitPreviewResponse = {
  league_id: string;
  user: string;
  generated_at: string;
  preview: ModelLabSplitPreview;
};

export type ModelRunArtifacts = {
  bundle_uri?: string | null;
  model_bundle_uri?: string | null;
  metrics_uri?: string | null;
  predictions_uri?: string | null;
};

export type ProbabilisticMetrics = {
  log_loss: number;
  brier_score: number;
  n_samples: number;
};

export type ModelRunMetrics = {
  baseline: ProbabilisticMetrics;
  custom: ProbabilisticMetrics;
};

export type ModelRun = {
  run_id: string;
  config_id: string;
  user_key: string;
  league_id: string;
  status: "pending" | "running" | "succeeded" | "failed";
  split: TimeSplit;
  artifacts: ModelRunArtifacts;
  metrics?: ModelRunMetrics | null;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  error?: string | null;
};

export type ActiveModel = {
  user_key: string;
  league_id: string;
  run_id: string | null;
  updated_at: string;
};

/** Account-backed UI preferences (API v1). Cosmetic only. */
export type SlateUiPreferences = {
  prob_style: "bar" | "number" | "gauge";
  table_density: "compact" | "default" | "relaxed";
  hide_empty_market: boolean;
  group_by_date: boolean;
  show_your_model: boolean;
  show_delta_pp: boolean;
};

export type ModelLabUiPreferences = {
  show_run_history: boolean;
  compact_sliders: boolean;
};

export type UiPreferencesV1 = {
  version: 1;
  league_id: string;
  user_key: string;
  slate: SlateUiPreferences;
  model_lab: ModelLabUiPreferences;
  updated_at: string;
};

export type UiPreferencesPatch = {
  slate?: Partial<SlateUiPreferences>;
  model_lab?: Partial<ModelLabUiPreferences>;
};

type RequestOpts = {
  idToken?: string;
  signal?: AbortSignal;
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function normalizeProbMetrics(raw: unknown): ProbabilisticMetrics | null {
  if (!isRecord(raw)) return null;
  const logLoss = raw.log_loss;
  const brier = raw.brier ?? raw.brier_score;
  const n = raw.n ?? raw.n_samples;
  if (
    typeof logLoss !== "number" ||
    typeof brier !== "number" ||
    typeof n !== "number"
  ) {
    return null;
  }
  if (
    !Number.isFinite(logLoss) ||
    !Number.isFinite(brier) ||
    !Number.isFinite(n)
  ) {
    return null;
  }
  return { log_loss: logLoss, brier_score: brier, n_samples: n };
}

function normalizeModelRunMetrics(raw: unknown): ModelRunMetrics | null {
  if (!isRecord(raw)) return null;
  const baseline = normalizeProbMetrics(raw.baseline);
  const custom = normalizeProbMetrics(raw.custom);
  if (!baseline || !custom) return null;
  return { baseline, custom };
}

function normalizeModelConfig(raw: unknown): ModelConfig | null {
  if (!isRecord(raw)) return null;
  const config_id = raw.config_id;
  const user_key = raw.user_key;
  const league_id = raw.league_id;
  const created_at = raw.created_at;
  if (
    typeof config_id !== "string" ||
    typeof user_key !== "string" ||
    typeof league_id !== "string" ||
    typeof created_at !== "string"
  ) {
    return null;
  }

  const fgwRaw = raw.feature_group_weights;
  const weights: FeatureGroupWeights = {};
  if (isRecord(fgwRaw) && isRecord(fgwRaw.weights)) {
    for (const [k, v] of Object.entries(fgwRaw.weights)) {
      if (typeof v === "number" && Number.isFinite(v)) weights[k] = v;
      else if (typeof v === "string" && v.trim() !== "") {
        const n = Number(v);
        if (Number.isFinite(n)) weights[k] = n;
      }
    }
  } else if (isRecord(fgwRaw)) {
    for (const [k, v] of Object.entries(fgwRaw)) {
      if (k === "version") continue;
      if (typeof v === "number" && Number.isFinite(v)) weights[k] = v;
      else if (typeof v === "string" && v.trim() !== "") {
        const n = Number(v);
        if (Number.isFinite(n)) weights[k] = n;
      }
    }
  }

  const display_name =
    typeof raw.display_name === "string" ? raw.display_name : null;
  const note = typeof raw.note === "string" ? raw.note : null;
  const label = typeof raw.label === "string" ? raw.label : display_name;

  return {
    config_id,
    user_key,
    league_id,
    label,
    display_name,
    note,
    feature_group_weights: weights,
    created_at,
  };
}

function normalizeModelRun(raw: unknown): ModelRun | null {
  if (!isRecord(raw)) return null;

  const run_id = raw.run_id;
  const config_id = raw.config_id;
  const user_key = raw.user_key;
  const league_id = raw.league_id;
  const status = raw.status;
  const split = raw.split;
  const created_at = raw.created_at;

  if (
    typeof run_id !== "string" ||
    typeof config_id !== "string" ||
    typeof user_key !== "string" ||
    typeof league_id !== "string" ||
    typeof status !== "string" ||
    !isRecord(split) ||
    typeof created_at !== "string"
  ) {
    return null;
  }

  if (
    status !== "pending" &&
    status !== "running" &&
    status !== "succeeded" &&
    status !== "failed"
  ) {
    return null;
  }

  const train_end_date = split.train_end_date;
  const eval_start_date = split.eval_start_date;
  const eval_end_date = split.eval_end_date;
  if (
    typeof train_end_date !== "string" ||
    typeof eval_start_date !== "string" ||
    typeof eval_end_date !== "string"
  ) {
    return null;
  }

  const artifactsRaw = raw.artifacts;
  const artifacts: ModelRunArtifacts = {};
  if (isRecord(artifactsRaw)) {
    if (typeof artifactsRaw.model_bundle_uri === "string") {
      artifacts.model_bundle_uri = artifactsRaw.model_bundle_uri;
      artifacts.bundle_uri = artifactsRaw.model_bundle_uri;
    }
    if (typeof artifactsRaw.bundle_uri === "string") {
      artifacts.bundle_uri = artifactsRaw.bundle_uri;
    }
    if (typeof artifactsRaw.metrics_uri === "string")
      artifacts.metrics_uri = artifactsRaw.metrics_uri;
    if (typeof artifactsRaw.predictions_uri === "string") {
      artifacts.predictions_uri = artifactsRaw.predictions_uri;
    }
  }

  const started_at = typeof raw.started_at === "string" ? raw.started_at : null;
  const completed_at =
    typeof raw.completed_at === "string" ? raw.completed_at : null;
  const updated_at =
    typeof raw.updated_at === "string"
      ? raw.updated_at
      : (completed_at ?? started_at ?? created_at);
  const error = typeof raw.error === "string" ? raw.error : null;

  const embeddedMetrics = normalizeModelRunMetrics(raw.metrics);

  return {
    run_id,
    config_id,
    user_key,
    league_id,
    status,
    split: { train_end_date, eval_start_date, eval_end_date },
    artifacts,
    metrics: embeddedMetrics,
    created_at,
    updated_at,
    started_at,
    completed_at,
    error,
  };
}

export function defaultSlateUiPreferences(): SlateUiPreferences {
  return {
    prob_style: "gauge",
    table_density: "compact",
    hide_empty_market: true,
    group_by_date: true,
    show_your_model: false,
    show_delta_pp: false,
  };
}

export function defaultModelLabUiPreferences(): ModelLabUiPreferences {
  return {
    show_run_history: true,
    compact_sliders: false,
  };
}

function normalizeSlateUiPreferences(raw: unknown): SlateUiPreferences | null {
  if (!isRecord(raw)) return null;
  const defaults = defaultSlateUiPreferences();
  const prob_style = raw.prob_style;
  const table_density = raw.table_density;
  const hide_empty_market = raw.hide_empty_market;
  const group_by_date = raw.group_by_date;
  if (
    prob_style !== "bar" &&
    prob_style !== "number" &&
    prob_style !== "gauge"
  ) {
    return null;
  }
  if (
    table_density !== "compact" &&
    table_density !== "default" &&
    table_density !== "relaxed"
  ) {
    return null;
  }
  if (
    typeof hide_empty_market !== "boolean" ||
    typeof group_by_date !== "boolean"
  ) {
    return null;
  }
  const show_your_model =
    typeof raw.show_your_model === "boolean"
      ? raw.show_your_model
      : defaults.show_your_model;
  const show_delta_pp =
    typeof raw.show_delta_pp === "boolean"
      ? raw.show_delta_pp
      : defaults.show_delta_pp;
  return {
    prob_style,
    table_density,
    hide_empty_market,
    group_by_date,
    show_your_model,
    show_delta_pp,
  };
}

function normalizeModelLabUiPreferences(
  raw: unknown,
): ModelLabUiPreferences | null {
  if (!isRecord(raw)) return null;
  const show_run_history = raw.show_run_history;
  const compact_sliders = raw.compact_sliders;
  if (
    typeof show_run_history !== "boolean" ||
    typeof compact_sliders !== "boolean"
  ) {
    return null;
  }
  return { show_run_history, compact_sliders };
}

export function normalizeUiPreferencesV1(raw: unknown): UiPreferencesV1 | null {
  if (!isRecord(raw)) return null;
  const version = raw.version;
  const league_id = raw.league_id;
  const user_key = raw.user_key;
  const updated_at = raw.updated_at;
  const slate = normalizeSlateUiPreferences(raw.slate);
  const model_lab = normalizeModelLabUiPreferences(raw.model_lab);
  if (
    version !== 1 ||
    typeof league_id !== "string" ||
    typeof user_key !== "string" ||
    typeof updated_at !== "string" ||
    !slate ||
    !model_lab
  ) {
    return null;
  }
  return { version: 1, league_id, user_key, slate, model_lab, updated_at };
}

function normalizeActiveModel(raw: unknown): ActiveModel | null {
  if (!isRecord(raw)) return null;
  const user_key = raw.user_key;
  const league_id = raw.league_id;
  const updated_at = raw.updated_at;
  const run_id = raw.run_id;
  if (
    typeof user_key !== "string" ||
    typeof league_id !== "string" ||
    typeof updated_at !== "string"
  ) {
    return null;
  }
  if (run_id !== null && typeof run_id !== "string") {
    return null;
  }
  return { user_key, league_id, run_id, updated_at };
}

async function request<T>(
  path: string,
  init: RequestInit & RequestOpts = {},
): Promise<T> {
  if (!config.apiBaseUrl) {
    throw new Error(
      "VITE_API_BASE_URL is not configured. Set it in your .env before calling the API.",
    );
  }
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (init.idToken) {
    headers.set("authorization", `Bearer ${init.idToken}`);
  }
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status} ${res.statusText}: ${text}`);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const api = {
  getSlate(opts: RequestOpts & SlateQueryOpts = {}): Promise<SlateResponse> {
    const { from, to, all, upcoming, ...fetchOpts } = opts;
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (all) params.set("all", "true");
    if (upcoming === false) params.set("upcoming", "false");
    const qs = params.toString();
    const path = `/v1/leagues/${encodeURIComponent(config.leagueId)}/slate${
      qs ? `?${qs}` : ""
    }`;
    return request<SlateResponse>(path, fetchOpts);
  },

  getPredictionHistory(
    opts: PredictionHistoryOpts = {},
  ): Promise<PredictionHistoryResponse> {
    const { season, allHistory, limit, ...fetchOpts } = opts;
    const params = new URLSearchParams();
    if (season) params.set("season", season);
    if (allHistory) params.set("all_history", "true");
    if (limit != null) params.set("limit", String(limit));
    const qs = params.toString();
    const path = `/v1/leagues/${encodeURIComponent(config.leagueId)}/prediction-history${
      qs ? `?${qs}` : ""
    }`;
    return request<PredictionHistoryResponse>(path, fetchOpts);
  },

  getResults(
    opts: { window?: ResultsWindow } & RequestOpts = {},
  ): Promise<ResultsResponse> {
    const { window, ...fetchOpts } = opts;
    const params = new URLSearchParams();
    if (window) params.set("window", window);
    const qs = params.toString();
    const path = `/v1/leagues/${encodeURIComponent(config.leagueId)}/results${
      qs ? `?${qs}` : ""
    }`;
    return request<ResultsResponse>(path, fetchOpts);
  },

  async createModelConfig(
    body: { label?: string; feature_group_weights: FeatureGroupWeights },
    opts: RequestOpts = {},
  ): Promise<ModelConfig> {
    const { label, feature_group_weights } = body;
    const res = await request<{ config: unknown }>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/model-configs`,
      {
        ...opts,
        method: "POST",
        body: JSON.stringify({
          league_id: config.leagueId,
          display_name: label,
          // Persisted schema uses ``{ version, weights }``; sending this shape avoids
          // ambiguous validation on the API.
          feature_group_weights: { version: 1, weights: feature_group_weights },
        }),
      },
    );
    const cfg = normalizeModelConfig(res.config);
    if (!cfg) {
      throw new Error("API returned an invalid model config payload.");
    }
    return cfg;
  },

  async createModelRun(
    body: { config_id: string; split?: TimeSplit },
    opts: RequestOpts = {},
  ): Promise<ModelRun> {
    const res = await request<{ run: unknown }>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/model-runs`,
      {
        ...opts,
        method: "POST",
        body: JSON.stringify({ league_id: config.leagueId, ...body }),
      },
    );
    const run = normalizeModelRun(res.run);
    if (!run) {
      throw new Error("API returned an invalid model run payload.");
    }
    return run;
  },

  previewModelLabSplit(
    body: { split?: TimeSplit } = {},
    opts: RequestOpts = {},
  ): Promise<ModelLabSplitPreviewResponse> {
    return request<ModelLabSplitPreviewResponse>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/model-lab/split-preview`,
      {
        ...opts,
        method: "POST",
        body: JSON.stringify({ league_id: config.leagueId, ...body }),
      },
    );
  },

  async getModelRun(runId: string, opts: RequestOpts = {}): Promise<ModelRun> {
    const res = await request<{ run: unknown; metrics: unknown }>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/model-runs/${encodeURIComponent(runId)}`,
      opts,
    );
    const run = normalizeModelRun(res.run);
    if (!run) {
      throw new Error("API returned an invalid model run payload.");
    }
    const gcsMetrics = normalizeModelRunMetrics(res.metrics);
    const embeddedMetrics = normalizeModelRunMetrics(run.metrics);
    // Never trust partially-shaped metrics objects from list/hydration paths.
    const metrics = gcsMetrics ?? embeddedMetrics ?? null;
    return { ...run, metrics };
  },

  async listModelConfigs(
    opts: RequestOpts & { limit?: number } = {},
  ): Promise<ModelConfig[]> {
    const { limit, ...fetchOpts } = opts;
    const params = new URLSearchParams();
    if (limit != null) params.set("limit", String(limit));
    const qs = params.toString();
    const res = await request<{ configs: unknown[] }>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/model-configs${qs ? `?${qs}` : ""}`,
      fetchOpts,
    );
    const out: ModelConfig[] = [];
    for (const c of res.configs ?? []) {
      const cfg = normalizeModelConfig(c);
      if (cfg) out.push(cfg);
    }
    return out;
  },

  async listModelRuns(
    opts: RequestOpts & { limit?: number } = {},
  ): Promise<ModelRun[]> {
    const { limit, ...fetchOpts } = opts;
    const params = new URLSearchParams();
    if (limit != null) params.set("limit", String(limit));
    const qs = params.toString();
    const res = await request<{ runs: unknown[] }>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/model-runs${qs ? `?${qs}` : ""}`,
      fetchOpts,
    );
    const out: ModelRun[] = [];
    for (const r of res.runs ?? []) {
      const run = normalizeModelRun(r);
      if (run) out.push(run);
    }
    return out;
  },

  async getActiveModel(opts: RequestOpts = {}): Promise<ActiveModel | null> {
    const res = await request<{ active_model: unknown }>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/active-model`,
      opts,
    );
    if (res.active_model == null) {
      return null;
    }
    return normalizeActiveModel(res.active_model);
  },

  async setActiveModel(
    body: { run_id: string },
    opts: RequestOpts = {},
  ): Promise<ActiveModel> {
    const res = await request<{ active_model: unknown }>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/active-model`,
      {
        ...opts,
        method: "POST",
        body: JSON.stringify({ league_id: config.leagueId, ...body }),
      },
    );
    const active = normalizeActiveModel(res.active_model);
    if (!active) {
      throw new Error("API returned an invalid active model payload.");
    }
    return active;
  },

  async getUiPreferences(opts: RequestOpts = {}): Promise<UiPreferencesV1> {
    const res = await request<{ ui_preferences: unknown }>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/me/ui-preferences`,
      opts,
    );
    const prefs = normalizeUiPreferencesV1(res.ui_preferences);
    if (!prefs) {
      throw new Error("API returned an invalid ui_preferences payload.");
    }
    return prefs;
  },

  async setUiPreferences(
    body: UiPreferencesPatch,
    opts: RequestOpts = {},
  ): Promise<UiPreferencesV1> {
    const res = await request<{ ui_preferences: unknown }>(
      `/v1/leagues/${encodeURIComponent(config.leagueId)}/me/ui-preferences`,
      {
        ...opts,
        method: "PUT",
        body: JSON.stringify(body),
      },
    );
    const prefs = normalizeUiPreferencesV1(res.ui_preferences);
    if (!prefs) {
      throw new Error("API returned an invalid ui_preferences payload.");
    }
    return prefs;
  },
};
